import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSyncWatch } from "../context/SyncContext";
import { Users, Car, CalendarDays, Package, Folder, Briefcase, Layers, Home, ArrowRight, AlertTriangle, AlertCircle, Droplet, Wrench, Calendar, AlertOctagon, Clock, Gauge, FileText, RefreshCw, ClipboardCheck, Wallet, ListTodo, Fuel, Truck } from "lucide-react";
import { EquipmentTransferCards } from "../components/EquipmentTransfer";
import { SparePartsBanner } from "../components/SparePartsBanner";
import { FuelModal } from "./EmployeeDashboard";
import { toast } from "sonner";
import { fmtRoDate } from "../lib/dateFmt";
import { isDoaga } from "../lib/chf";
import VideoExplicativeCard from "../components/VideoExplicativeCard";
import AssignedTasksBanner from "../components/AssignedTasksBanner";
import MissingHoursBanner from "../components/MissingHoursBanner";
import OrphanHoursBanner from "../components/OrphanHoursBanner";
import PendingDismantlesBanner from "../components/PendingDismantlesBanner";
import DeclaredAbuseBanner from "../components/DeclaredAbuseBanner";
import FuelAnomalyBanner from "../components/FuelAnomalyBanner";
import MaintenanceMissingBanner from "../components/MaintenanceMissingBanner";
import AdminMaintenanceCard from "../components/AdminMaintenanceCard";
import UnbilledBanner from "../components/UnbilledBanner";
import TasksAdminPanel from "../components/TasksAdminPanel";
import ComplianceBanner from "../components/ComplianceBanner";
import EmployeeStatsPanel from "./dashboard/EmployeeStatsPanel";
import DigestPanel from "./dashboard/DigestPanel";
import UpcomingProjectsPanel from "./dashboard/UpcomingProjectsPanel";
import MissingDataBanners from "./dashboard/MissingDataBanners";
import AuditCoverageBanner from "./dashboard/AuditCoverageBanner";
import AlertsPanel from "./dashboard/AlertsPanel";
import { useMissingSummary } from "./dashboard/useMissingSummary";
import QuickAdminHoursDialog from "../components/QuickAdminHoursDialog";
import SimpleHoursCard from "../components/SimpleHoursCard";

const ICONS = { users: Users, car: Car, calendar: CalendarDays, package: Package, folder: Folder, briefcase: Briefcase, layers: Layers, home: Home, wallet: Wallet, truck: Truck, fuel: Fuel };
const ROUTES = {
  PERSONAL: "/personal", AUTOTURISME: "/autoturisme", REMORCI: "/remorci", PLANIFICATOR_CONCEDIU: "/concediu",
  INVENTAR: "/inventar", PROIECTE: "/proiecte", MANAGEMENT: "/management", CHIRII: "/chirii", AVANSURI: "/avansuri",
  PLANIFICATOR: "/planificator", CHIP_ALIMENTARE: "/chip-alimentare",
};

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const [modules, setModules] = useState([]);
  const [stats, setStats] = useState({ persons: 0, cars: 0, notifs: 0, projects: 0 });
  const [alerts, setAlerts] = useState(null);
  const [digest, setDigest] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);
  const [myTasks, setMyTasks] = useState(0);
  const [fuelOpen, setFuelOpen] = useState(false);
  const [quickHoursOpen, setQuickHoursOpen] = useState(false);
  const navigate = useNavigate();

  const loadAll = async () => {
    const m = await api.get("/modules");
    setModules(m.data);
    const [p, c, n, pr, al] = await Promise.all([
      api.get("/persons").catch(() => ({ data: [] })),
      api.get("/cars").catch(() => ({ data: [] })),
      api.get("/notifications").catch(() => ({ data: [] })),
      api.get("/projects").catch(() => ({ data: [] })),
      api.get("/dashboard/alerts").catch(() => ({ data: null })),
    ]);
    setStats({ persons: p.data.length, cars: c.data.length, notifs: n.data.length, projects: pr.data.length });
    setAlerts(al.data);
    // Pending tasks count (employees see their own; admin gets global pending)
    api.get("/tasks?status=pending").then((r) => setMyTasks((r.data || []).length)).catch(() => {});
    if (isAdmin) {
      // auto-check (idempotent per ISO week)
      api.post("/digest/check").then((r) => setDigest(r.data?.digest)).catch(() => {});
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [isAdmin]);
  useSyncWatch(["projects", "regii", "schedule", "vacations", "inventory", "persons"], () => { loadAll(); });

  const regenerateDigest = async () => {
    setDigestLoading(true);
    try {
      const { data } = await api.post("/digest/run");
      setDigest(data);
    } catch {} finally { setDigestLoading(false); }
  };

  return (
    <div data-testid="dashboard">
      <SparePartsBanner />
      <ComplianceBanner />
      <AssignedTasksBanner />
      <div className="mb-4"><SimpleHoursCard /></div>
      {isAdmin && <MissingHoursBanner scope="dashboard" />}
      {(isAdmin || user?.role === "planificator") && <PendingDismantlesBanner />}
      {isAdmin && <DeclaredAbuseBanner />}
      {isAdmin && <FuelAnomalyBanner />}
      {isAdmin && <MaintenanceMissingBanner />}
      {isAdmin && <UnbilledBanner />}
      {isAdmin && <OrphanHoursBanner />}
      <div className="flex items-end justify-between mb-6">
        <div className="flex items-start gap-5">
          {/* Round company logo (hidden for admin to keep clean exec view) */}
          {!isAdmin && (
            <a
              href="https://www.convicgmbh.ch"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:block group/logo flex-shrink-0 mt-1"
              data-testid="dashboard-logo"
              title="ConVic GmbH"
            >
              <div className="relative">
                <div className="absolute -inset-1 rounded-full border border-[var(--primary)]/25 group-hover/logo:border-[var(--primary)]/70 transition-colors duration-500" aria-hidden="true" />
                <div className="relative w-16 h-16 rounded-full overflow-hidden ring-1 ring-zinc-200 bg-white shadow-md transition-transform duration-500 group-hover/logo:scale-[1.04]">
                  <img
                    src="/logo-convic.jpg"
                    alt="ConVic"
                    className="w-full h-full object-cover scale-[1.02] select-none"
                    draggable="false"
                  />
                </div>
              </div>
            </a>
          )}
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
              {(() => {
                const name = String(user?.name || user?.username || "").toLowerCase();
                if (name.includes("victor")) return "Să trăiți Don Director !";
                // Prefer prenume din persoana legată; altfel primul cuvânt din name
                const firstName = user?.prenume
                  || (user?.name ? String(user.name).trim().split(/\s+/)[0] : "")
                  || user?.username
                  || "";
                return `Neața, ${firstName} !`;
              })()}
            </h1>
            <p className="text-sm text-zinc-500 mt-2">Prezentare generală a operațiunilor ConVic</p>
          </div>
        </div>
      </div>

      {/* TASK card — afișat imediat sub salut */}
      {isAdmin && (
        <button
          data-testid="quick-tasks-top"
          onClick={() => navigate("/tasks")}
          className="card-flat p-6 text-left hover:border-[#16A34A] transition-colors group border-l-4 border-l-[#16A34A] w-full mb-4 block"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-md bg-[#DCFCE7] text-[#16A34A] grid place-items-center shrink-0">
                <ListTodo size={22}/>
              </div>
              <div>
                <div className="small-label text-[#16A34A]">Acțiune rapidă</div>
                <div className="font-display text-xl font-bold mt-1">TASK</div>
                <div className="text-xs text-zinc-500 mt-1">Atribuie sarcini angajaților</div>
              </div>
            </div>
            <ArrowRight size={18} className="text-zinc-400 group-hover:text-[#16A34A] transition-colors mt-2"/>
          </div>
        </button>
      )}
      {/* ÎNTREȚINERE MAȘINI — card sub TASK cu rapoartele șoferilor */}
      {isAdmin && <AdminMaintenanceCard />}
      {/* ALIMENTEAZĂ — admin top */}
      {isAdmin && (
        <button
          data-testid="quick-alimenteaza-top"
          onClick={() => setFuelOpen(true)}
          className="card-flat p-6 text-left hover:border-[#10B981] transition-colors group border-l-4 border-l-[#10B981] w-full mb-4 block"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-md bg-[#D1FAE5] text-[#059669] grid place-items-center shrink-0">
                <Fuel size={22}/>
              </div>
              <div>
                <div className="small-label text-[#059669]">Acțiune rapidă</div>
                <div className="font-display text-xl font-bold mt-1">ALIMENTEAZĂ</div>
                <div className="text-xs text-zinc-500 mt-1">Adaugă o alimentare la orice autoturism din flotă</div>
              </div>
            </div>
            <ArrowRight size={18} className="text-zinc-400 group-hover:text-[#10B981] transition-colors mt-2"/>
          </div>
        </button>
      )}
      {/* COMBUSTIBIL — admin top */}
      {isAdmin && (
        <button
          data-testid="quick-combustibil-top"
          onClick={() => navigate("/combustibil")}
          className="card-flat p-6 text-left hover:border-[#D97706] transition-colors group border-l-4 border-l-[#D97706] w-full mb-4 block"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-md bg-[#FEF3C7] text-[#D97706] grid place-items-center shrink-0">
                <Droplet size={22}/>
              </div>
              <div>
                <div className="small-label text-[#D97706]">Acțiune rapidă</div>
                <div className="font-display text-xl font-bold mt-1">COMBUSTIBIL</div>
                <div className="text-xs text-zinc-500 mt-1">Vezi istoric și rapoarte combustibil</div>
              </div>
            </div>
            <ArrowRight size={18} className="text-zinc-400 group-hover:text-[#D97706] transition-colors mt-2"/>
          </div>
        </button>
      )}
      {/* ORE — admin top */}
      {isAdmin && !isDoaga(user) && (
        <button
          data-testid="quick-ore-admin-top"
          onClick={() => navigate("/ore")}
          className="card-flat p-6 text-left hover:border-[var(--primary)] transition-colors group border-l-4 border-l-[var(--primary)] w-full mb-8 block"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-md bg-[var(--primary-subtle)] text-[var(--primary)] grid place-items-center shrink-0">
                <CalendarDays size={22}/>
              </div>
              <div>
                <div className="small-label text-[var(--primary)]">Acțiune rapidă</div>
                <div className="font-display text-xl font-bold mt-1">ORE</div>
                <div className="text-xs text-zinc-500 mt-1">Calendarul orelor pentru toți angajații</div>
              </div>
            </div>
            <ArrowRight size={18} className="text-zinc-400 group-hover:text-[var(--primary)] transition-colors mt-2"/>
          </div>
        </button>
      )}
      {/* ISTORIC REPARAȚII — admin top */}
      {isAdmin && (
        <button
          data-testid="quick-istoric-reparatii"
          onClick={() => navigate("/istoric-reparatii")}
          className="card-flat p-6 text-left hover:border-[#EA580C] transition-colors group border-l-4 border-l-[#EA580C] w-full mb-4 block"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-md bg-[#FFEDD5] text-[#EA580C] grid place-items-center shrink-0">
                <Wrench size={22}/>
              </div>
              <div>
                <div className="small-label text-[#EA580C]">Acțiune rapidă</div>
                <div className="font-display text-xl font-bold mt-1 leading-tight">ISTORIC AUTOTURISME &amp; ECHIPAMENTE REPARATE</div>
                <div className="text-xs text-zinc-500 mt-1">Toate reparațiile și reviziile efectuate — cine, când, ce piese</div>
              </div>
            </div>
            <ArrowRight size={18} className="text-zinc-400 group-hover:text-[#EA580C] transition-colors mt-2"/>
          </div>
        </button>
      )}
      {/* DOCUMENTE — admin only, arhivă privată */}
      {isAdmin && (
        <button
          data-testid="quick-documente-admin"
          onClick={() => navigate("/documente")}
          className="card-flat p-6 text-left hover:border-blue-500 transition-colors group border-l-4 border-l-blue-500 w-full mb-8 block"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-md bg-blue-100 text-blue-700 grid place-items-center shrink-0">
                <FileText size={22}/>
              </div>
              <div>
                <div className="small-label text-blue-700">Arhivă privată admin</div>
                <div className="font-display text-xl font-bold mt-1">DOCUMENTE</div>
                <div className="text-xs text-zinc-500 mt-1">Încarcă contracte, facturi, taloane, autorizații — orice tip de fișier (PDF/Word/Excel/imagini/zip)</div>
              </div>
            </div>
            <ArrowRight size={18} className="text-zinc-400 group-hover:text-blue-600 transition-colors mt-2"/>
          </div>
        </button>
      )}
      {!isAdmin && user?.person_id && (myTasks > 0) && (
        <button
          data-testid="quick-my-tasks-top"
          onClick={() => navigate("/tasks")}
          className="card-flat p-6 text-left hover:border-[#16A34A] transition-colors group border-l-4 border-l-[#16A34A] w-full mb-8 block"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-md bg-[#DCFCE7] text-[#16A34A] grid place-items-center shrink-0">
                <ListTodo size={22}/>
              </div>
              <div>
                <div className="small-label text-[#16A34A]">Sarcinile mele</div>
                <div className="font-display text-xl font-bold mt-1">{myTasks} TASK{myTasks === 1 ? "" : "-uri"}</div>
                <div className="text-xs text-zinc-500 mt-1">Click pentru a vedea și marca efectuat</div>
              </div>
            </div>
            <ArrowRight size={18} className="text-zinc-400 group-hover:text-[#16A34A] transition-colors mt-2"/>
          </div>
        </button>
      )}

      {/* Equipment transfer cards (admin only) */}
      {isAdmin && (
        <div className="mb-8">
          <EquipmentTransferCards />
        </div>
      )}

      {isAdmin && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard label="Personal" value={stats.persons} color="#0044FF" onClick={() => navigate("/personal")} testId="stat-personal" />
          <StatCard label="Autoturisme" value={stats.cars} color="#00B84A" onClick={() => navigate("/autoturisme")} testId="stat-autoturisme" />
          <StatCard label="Proiecte" value={stats.projects} color="#FF8A00" onClick={() => navigate("/proiecte")} testId="stat-proiecte" />
          <StatCard label="Notificări active" value={stats.notifs} color="#FF2A2A" onClick={() => navigate("/management")} testId="stat-notifs" />
        </div>
      )}

      {alerts && <AlertsPanel alerts={alerts} navigate={navigate} />}

      {isAdmin && <UpcomingProjectsPanel navigate={navigate} />}

      {isAdmin && <EmployeeStatsPanel />}

      {isAdmin && digest && <DigestPanel digest={digest} onRegenerate={regenerateDigest} loading={digestLoading} navigate={navigate}/>}

      <h2 className="font-display text-xl font-bold mb-4">Acțiuni rapide</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {isAdmin && (
          <button
            data-testid="admin-quick-hours"
            onClick={() => setQuickHoursOpen(true)}
            className="card-flat p-6 text-left hover:border-emerald-600 transition-colors group border-l-4 border-l-emerald-600"
          >
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 rounded-md bg-emerald-50 text-emerald-700 grid place-items-center">
                <Clock size={22}/>
              </div>
              <ArrowRight size={18} className="text-zinc-400 group-hover:text-emerald-700 transition-colors"/>
            </div>
            <div className="mt-6">
              <div className="small-label text-emerald-700">Acțiune rapidă</div>
              <div className="font-display text-xl font-bold mt-1">⏱️ ORE</div>
              <div className="text-xs text-zinc-500 mt-1">Bagă rapid ore pe șantier / regie / magazie pentru orice persoane</div>
            </div>
          </button>
        )}
        {!isAdmin && (
          <button
            data-testid="quick-combustibil"
            onClick={() => navigate("/combustibil")}
            className="card-flat p-6 text-left hover:border-[#D97706] transition-colors group border-l-4 border-l-[#D97706]"
          >
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 rounded-md bg-[#FEF3C7] text-[#D97706] grid place-items-center">
                <Droplet size={22}/>
              </div>
              <ArrowRight size={18} className="text-zinc-400 group-hover:text-[#D97706] transition-colors"/>
            </div>
            <div className="mt-6">
              <div className="small-label text-[#D97706]">Acțiune rapidă</div>
              <div className="font-display text-xl font-bold mt-1">COMBUSTIBIL</div>
              <div className="text-xs text-zinc-500 mt-1">Vezi istoric și rapoarte combustibil</div>
            </div>
          </button>
        )}
        {!isAdmin && user?.person_id && (
          <>
            <button
              data-testid="quick-ore"
              onClick={() => navigate(`/personal/${user.person_id}#calendar`)}
              className="card-flat p-6 text-left hover:border-[var(--primary)] transition-colors group border-l-4 border-l-[var(--primary)]"
            >
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 rounded-md bg-[var(--primary-subtle)] text-[var(--primary)] grid place-items-center">
                  <CalendarDays size={22}/>
                </div>
                <ArrowRight size={18} className="text-zinc-400 group-hover:text-[var(--primary)] transition-colors"/>
              </div>
              <div className="mt-6">
                <div className="small-label text-[var(--primary)]">Acțiune rapidă</div>
                <div className="font-display text-xl font-bold mt-1">ORE</div>
                <div className="text-xs text-zinc-500 mt-1">Notează orele lucrate astăzi</div>
              </div>
            </button>
            <button
              data-testid="quick-cheltuieli"
              onClick={() => navigate(`/personal/${user.person_id}#cheltuieli`)}
              className="card-flat p-6 text-left hover:border-[#7C3AED] transition-colors group border-l-4 border-l-[#7C3AED]"
            >
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 rounded-md bg-[#EDE9FE] text-[#7C3AED] grid place-items-center">
                  <Wallet size={22}/>
                </div>
                <ArrowRight size={18} className="text-zinc-400 group-hover:text-[#7C3AED] transition-colors"/>
              </div>
              <div className="mt-6">
                <div className="small-label text-[#7C3AED]">Acțiune rapidă</div>
                <div className="font-display text-xl font-bold mt-1">CHELTUIELI</div>
                <div className="text-xs text-zinc-500 mt-1">Trimite o cheltuială pentru aprobare</div>
              </div>
            </button>
          </>
        )}
      </div>

      <h2 className="font-display text-xl font-bold mb-4">Module</h2>
      {isAdmin && <MissingDataBanners navigate={navigate} />}
      {isAdmin && <AuditCoverageBanner navigate={navigate} />}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {modules.filter((m) => isAdmin || m.code !== "CHIP_ALIMENTARE").map((m) => {
          const Icon = ICONS[m.icon] || Layers;
          const route = ROUTES[m.code] || `/modul/${m.code}`;
          return (
            <button
              key={m.code}
              data-testid={`module-card-${m.code.toLowerCase()}`}
              onClick={() => navigate(route)}
              className="card-flat p-6 text-left hover:border-[var(--primary)] transition-colors group"
            >
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 rounded-md bg-[var(--primary-subtle)] text-[var(--primary)] grid place-items-center">
                  <Icon size={22} />
                </div>
                <ArrowRight size={18} className="text-zinc-400 group-hover:text-[var(--primary)] transition-colors" />
              </div>
              <div className="mt-6">
                <div className="small-label">Modul</div>
                <div className="font-display text-xl font-bold mt-1">{m.name}</div>
                <ModuleMissingCount code={m.code} />
              </div>
            </button>
          );
        })}
        {!isAdmin && (
          <button
            data-testid="module-card-sarcini"
            onClick={() => navigate("/tasks")}
            className="card-flat p-6 text-left hover:border-[#16A34A] transition-colors group"
          >
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 rounded-md bg-[#DCFCE7] text-[#16A34A] grid place-items-center">
                <ListTodo size={22} />
              </div>
              <ArrowRight size={18} className="text-zinc-400 group-hover:text-[#16A34A] transition-colors" />
            </div>
            <div className="mt-6">
              <div className="small-label">Modul</div>
              <div className="font-display text-xl font-bold mt-1">Sarcini</div>
            </div>
          </button>
        )}
      </div>
      {fuelOpen && <FuelModal onClose={() => setFuelOpen(false)} />}
      <QuickAdminHoursDialog open={quickHoursOpen} onClose={() => setQuickHoursOpen(false)} />
    </div>
  );
}

// Singleton cache pentru missing-data summary (un singur fetch per render Dashboard)
let _missingCacheStub = null; // Removed — moved to ./dashboard/useMissingSummary.js

// Map module-code → key în payload + label scurt
const MODULE_MISSING_MAP = {
  PERSONAL: { key: "persons", label: "persoane" },
  AUTOTURISME: { key: "cars", label: "mașini" },
  PROIECTE: { key: "projects", labelExtra: ["projects", "regii"], label: "proiecte+regii" },
};

function ModuleMissingCount({ code }) {
  const data = useMissingSummary();
  const cfg = MODULE_MISSING_MAP[code];
  if (!cfg || !data) return null;
  let total = 0;
  if (cfg.labelExtra) {
    for (const k of cfg.labelExtra) total += (data[k] || 0);
  } else {
    total = data[cfg.key] || 0;
  }
  if (total === 0) return null;
  return (
    <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200" data-testid={`module-missing-${code.toLowerCase()}`}>
      <span>⚠</span>
      <span>{total} {cfg.label} cu date lipsă</span>
    </div>
  );
}

function StatCard({ label, value, color, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="card-flat p-5 text-left hover:shadow-md hover:border-zinc-300 transition-all group"
      style={{ cursor: onClick ? "pointer" : "default" }}
    >
      <div className="small-label group-hover:text-zinc-700 transition-colors">{label}</div>
      <div className="font-display text-3xl font-extrabold mt-2 group-hover:scale-105 transition-transform origin-left" style={{ color }}>{value}</div>
    </button>
  );
}

